import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '@environments';

const TRANSLATION_CACHE_KEY = 'TRANSLATION_OBJECT_';
const FORBIDDEN_WORDS_KEY = 'forbidden_words';
const API_URL = `${environment.apiPath}api/i18n/v1/translations`;

/**
 * Loader custom para ngx-translate.
 *
 * Usa localStorage (no CacheManagerService) intencionalmente:
 * las traducciones deben ser compartidas entre tabs y persistir
 * al cerrar el navegador para mejorar el tiempo de carga inicial.
 */
@Injectable({ providedIn: 'root' })
export class CustomTranslateLoader implements TranslateLoader {
  private readonly http = inject(HttpClient);

  getTranslation(lang: string): Observable<Record<string, string>> {
    const apiTranslation$ = this.getApiTranslation(lang);
    const fileTranslation$ = this.getFileTranslation(lang);
    const cachedTranslation$ = this.getCachedTranslation(lang);

    return forkJoin([apiTranslation$, fileTranslation$, cachedTranslation$]).pipe(
      map(([api, file, cached]) => ({ ...file, ...cached, ...api })),
      catchError(() => of({}))
    );
  }

  private getApiTranslation(lang: string): Observable<Record<string, string>> {
    const url = `${API_URL}/${lang}?channel=all&project=web`;
    return this.http.get<Record<string, string>>(url).pipe(
      map((result) => {
        this.storeForbiddenWords(result);
        this.setCachedTranslation(lang, result);
        return result ?? {};
      }),
      catchError(() => of({}))
    );
  }

  private getFileTranslation(lang: string): Observable<Record<string, string>> {
    const url = `./assets/i18n/strings-${lang}.json?d=${Date.now()}`;
    return this.http.get<Record<string, string>>(url).pipe(
      map((result) => result ?? {}),
      catchError(() => of({}))
    );
  }

  private getCachedTranslation(lang: string): Observable<Record<string, string>> {
    const raw = this.readFromStorage(`${TRANSLATION_CACHE_KEY}${lang}`);
    if (!raw) {
      return of({});
    }
    try {
      return of(JSON.parse(raw) as Record<string, string>);
    } catch {
      return of({});
    }
  }

  private setCachedTranslation(lang: string, data: Record<string, string>): void {
    this.writeToStorage(`${TRANSLATION_CACHE_KEY}${lang}`, JSON.stringify(data));
  }

  private storeForbiddenWords(result: Record<string, string>): void {
    if (result[FORBIDDEN_WORDS_KEY]) {
      this.writeToStorage(FORBIDDEN_WORDS_KEY, result[FORBIDDEN_WORDS_KEY]);
    }
  }

  private readFromStorage(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writeToStorage(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // localStorage lleno o no disponible — ignorar silenciosamente
    }
  }
}
